import { useEffect, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { listingsAPI, offersAPI, paymentsAPI } from '../api/client'
import { publicAssetUrl } from '../utils/assetUrl'
import { hasListingPhotos } from '../utils/stockPhoneImage'
import ListingNoPhotoPlaceholder from './ListingNoPhotoPlaceholder'
import { inBoxLabelKey } from '../utils/inBoxCompleteness'
import MyListingDetailPanel from './MyListingDetailPanel'
import MyListingPhotosManager from './MyListingPhotosManager'
import ListingEditForm from './ListingEditForm'

interface ListingImage {
  id: number
  thumbnail_url: string
  display_url?: string
  is_primary: boolean
}

interface Listing {
  id: number
  title: string
  description?: string | null
  price: number
  condition: string
  created_at: string
  is_sold?: boolean
  is_reserved?: boolean
  reserved_for_user_id?: number | null
  battery_health?: number | null
  battery_cycles?: number | null
  battery_original?: boolean | null
  battery_tested_at?: string | null
  imei?: string
  has_factory_warranty?: boolean
  warranty_remaining?: string | null
  usage_duration?: string | null
  is_smartphone?: boolean
  release_year?: number | null
  in_box_completeness?: string
  shipment_method?: string
  defect_marks?: Array<{ x: number; y: number; label: string }>
  parts_authenticity?: Record<string, { status?: string; grade?: string | null }> | null
  phone_model: { manufacturer: string; model_name: string; hero_image_path?: string | null; release_year?: number | null }
  images?: ListingImage[]
}

interface Offer {
  id: number
  listing_id: number
  buyer_id: number
  offered_price: number
  is_accepted: boolean
  is_rejected: boolean
  created_at: string
  offer_kind?: string
  exchange_offered_listing_model?: string | null
  exchange_extra_cash?: number | null
}

interface MyListingsProps {
  token: string
  onOpenOfferDetail?: (offerId: number) => void
}

interface MineTransactionRow {
  id: number
  listing_id: number
  role: string
  status: string
  carrier?: string | null
  tracking_number?: string | null
}

function maskImei(digits: string | undefined): string {
  if (!digits) return '—'
  const d = digits.replace(/\D/g, '')
  if (d.length >= 4) return `…${d.slice(-4)}`
  return '—'
}

import { conditionColor } from '@redevicer/shared'

function thumbFor(listing: Listing): { src: string | null; isStock: boolean } {
  if (hasListingPhotos(listing.images)) {
    const primary = listing.images!.find((i) => i.is_primary)
    const img = primary ?? listing.images![0]
    return {
      src: publicAssetUrl(img.display_url || img.thumbnail_url || ''),
      isStock: false,
    }
  }
  return { src: null, isStock: false }
}


function ListingOffers({
  listingId,
  askingPrice,
  token,
  onAccepted,
  onOpenOfferDetail,
}: {
  listingId: number
  askingPrice: number
  token: string
  onAccepted: () => void
  onOpenOfferDetail?: (offerId: number) => void
}) {
  const { t } = useTranslation()
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)

  useEffect(() => {
    setLoading(true)
    offersAPI
      .getOffersForListing(listingId, token)
      .then((res) => setOffers(res.data))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false))
  }, [listingId, token])

  function accept(offerId: number) {
    setBusyId(offerId)
    offersAPI
      .acceptOffer(offerId, token)
      .then(() => offersAPI.getOffersForListing(listingId, token))
      .then((res) => setOffers(res.data))
      .then(() => onAccepted())
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusyId(null))
  }

  function reject(offerId: number) {
    setBusyId(offerId)
    offersAPI
      .rejectOffer(offerId, token)
      .then(() => offersAPI.getOffersForListing(listingId, token))
      .then((res) => setOffers(res.data))
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusyId(null))
  }

  if (loading) {
    return <p className="seller-offers-loading">{t('seller.loadingOffers')}</p>
  }

  if (offers.length === 0) {
    return <p className="seller-offers-empty">{t('seller.noOffersYet')}</p>
  }

  return (
    <ul className="seller-offers-list">
      {offers.map((offer) => {
        const acceptedElsewhere =
          offers.some((o) => o.is_accepted) && !offer.is_accepted

        return (
          <li key={offer.id} className="seller-offer-row">
            <div className="seller-offer-meta">
              {offer.offer_kind === 'exchange' && (
                <span className="seller-offer-badge seller-offer-badge--exchange">{t('offerDetail.exchangeBadge')}</span>
              )}
              <span className="seller-offer-price">€{offer.offered_price.toFixed(2)}</span>
              {offer.offer_kind === 'exchange' && offer.exchange_offered_listing_model && (
                <span className="seller-offer-exchange-line">
                  {offer.exchange_offered_listing_model}
                  {(offer.exchange_extra_cash ?? 0) > 0 && ` + €${(offer.exchange_extra_cash ?? 0).toFixed(2)}`}
                </span>
              )}
              <span className="seller-offer-buyer">
                {t('seller.buyer')} #{offer.buyer_id}
              </span>
              <span className="seller-offer-date">{new Date(offer.created_at).toLocaleString()}</span>
            </div>
            <div className="seller-offer-actions">
              <button
                type="button"
                className="btn-offer-view"
                onClick={() => onOpenOfferDetail?.(offer.id)}
              >
                {t('offerChats.openChat')}
              </button>
              {offer.is_accepted && <span className="seller-status accepted">{t('seller.accepted')}</span>}
              {!offer.is_accepted && !offer.is_rejected && !acceptedElsewhere && (
                <>
                  {askingPrice > 0 && (
                    <span className="seller-discount">
                      {((1 - offer.offered_price / askingPrice) * 100).toFixed(0)}% {t('seller.vsAsk')}
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn-accept-offer"
                    disabled={busyId !== null}
                    onClick={() => accept(offer.id)}
                  >
                    {busyId === offer.id ? '…' : t('seller.acceptOffer')}
                  </button>
                  <button
                    type="button"
                    className="btn-reject-offer"
                    disabled={busyId !== null}
                    onClick={() => reject(offer.id)}
                  >
                    {busyId === offer.id ? '…' : t('seller.rejectOffer')}
                  </button>
                </>
              )}
              {!offer.is_accepted && acceptedElsewhere && (
                <span className="seller-status superseded">{t('seller.notChosen')}</span>
              )}
              {offer.is_rejected && !offer.is_accepted && (
                <span className="seller-status rejected">{t('seller.passed')}</span>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default function MyListings({ token, onOpenOfferDetail }: MyListingsProps) {
  const { t } = useTranslation()
  const [listings, setListings] = useState<Listing[]>([])
  const [myDeals, setMyDeals] = useState<MineTransactionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [tick, setTick] = useState(0)
  const [editId, setEditId] = useState<number | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  function dealAsSellerForListing(listingId: number): MineTransactionRow | undefined {
    return myDeals.find((x) => x.listing_id === listingId && x.role === 'seller')
  }

  function startEdit(listing: Listing) {
    setError('')
    setEditId(listing.id)
  }

  function removeListing(listingId: number) {
    if (!window.confirm(t('myListings.deleteConfirm'))) return
    setBusyId(listingId)
    listingsAPI
      .deleteListing(listingId, token)
      .then(() => setTick((x) => x + 1))
      .catch((err) => setError(err.response?.data?.detail || 'Delete failed'))
      .finally(() => setBusyId(null))
  }

  function reload() {
    setLoading(true)
    setError('')
    Promise.all([listingsAPI.getMyListings(token), paymentsAPI.getMyTransactions(token)])
      .then(([lRes, tRes]) => {
        setListings(lRes.data)
        setMyDeals(tRes.data)
      })
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load your listings'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [token, tick])

  return (
    <section className="my-section my-listings-page my-listings-page--modern app-page app-page--narrow">
      <header className="my-listings-page__hero">
        <div className="my-listings-page__hero-glow" aria-hidden="true" />
        <div className="my-listings-page__head">
          <h2>{t('menu.myListings', 'My listings')}</h2>
          <button type="button" className="btn-text-refresh" onClick={() => setTick((x) => x + 1)}>
            {t('catalog.refresh')}
          </button>
        </div>
      </header>
      {loading && <p className="my-listings-page__meta">{t('common.loading', 'Loading...')}</p>}
      {error && <p className="error-text">{error}</p>}
      {!loading && listings.length === 0 && (
        <div className="my-listings-empty">
          <p>{t('myListings.empty', 'You have no listings yet.')}</p>
        </div>
      )}
      <div className="seller-listings seller-listings--modern">
        {listings.map((listing, listingIndex) => {
          const soldDeal = listing.is_sold ? dealAsSellerForListing(listing.id) : undefined
          const soldSummary =
            soldDeal &&
            [
              `${t('deals.transaction')} #${soldDeal.id}`,
              soldDeal.status,
              soldDeal.carrier ? `${t('deals.carrier')}: ${soldDeal.carrier}` : null,
              soldDeal.tracking_number ? `${t('deals.trackingNumber')}: ${soldDeal.tracking_number}` : null,
            ]
              .filter(Boolean)
              .join(' · ')
          const th = thumbFor(listing)

          return (
            <article
              key={listing.id}
              className="seller-listing-card seller-listing-card--modern"
              style={{ '--listing-i': listingIndex } as CSSProperties}
            >
              <details className="seller-listing-accordion seller-listing-accordion--modern ui-collapse-details">
                <summary className="seller-listing-accordion__summary">
                  <span className="seller-listing-accordion__thumb">
                    {th.src ? (
                      <img
                        src={th.src}
                        alt=""
                        title={th.isStock ? t('listing.stockPhotoTitle') : undefined}
                      />
                    ) : (
                      <ListingNoPhotoPlaceholder variant="thumb" />
                    )}
                  </span>
                  <span className="seller-listing-accordion__info">
                    {listing.is_sold && (
                      <span className="sold-badge seller-listing-accordion__sold">{t('seller.sold')}</span>
                    )}
                    {!listing.is_sold && listing.is_reserved ? (
                      <span className="reserved-badge seller-listing-accordion__reserved">{t('listing.reservedBadge')}</span>
                    ) : null}
                    <span className="seller-listing-accordion__brand">{listing.phone_model.manufacturer}</span>
                    <span className="seller-listing-accordion__model">{listing.phone_model.model_name}</span>
                    <span className="seller-listing-accordion__title">{listing.title}</span>
                    <span className="seller-listing-accordion__meta">
                      <span
                        className="seller-listing-condition-pill"
                        style={{ backgroundColor: conditionColor(listing.condition) }}
                      >
                        {t(`listing.condition.${listing.condition}`, listing.condition)}
                      </span>
                    </span>
                  </span>
                  <span className="seller-listing-accordion__price-block">
                    <span className="seller-listing-accordion__price">€{listing.price.toFixed(2)}</span>
                  </span>
                </summary>

                <div className="ui-collapse-details__panel">
                <div className="ui-collapse-details__inner seller-listing-accordion__body seller-listing-accordion__body--modern seller-listing-accordion__body--catalog">
                  <MyListingDetailPanel
                    listing={listing}
                    token={token}
                    editing={editId === listing.id}
                    busy={busyId === listing.id}
                    soldSummary={soldSummary || undefined}
                    onEdit={() => startEdit(listing)}
                    onDelete={() => removeListing(listing.id)}
                    onCancelEdit={() => setEditId(null)}
                    onSaved={() => {
                      setEditId(null)
                      setTick((x) => x + 1)
                    }}
                    onError={setError}
                    onPhotosChange={() => reload()}
                    onReservationChange={() => reload()}
                    offersSlot={
                      !listing.is_sold ? (
                        <details className="seller-listing-collapse seller-listing-offers-collapse seller-listing-collapse--modern ui-collapse-details">
                          <summary className="seller-listing-collapse__summary seller-listing-collapse__summary--modern">
                            {t('myListings.sectionOffers')}
                          </summary>
                          <div className="ui-collapse-details__panel">
                          <div className="ui-collapse-details__inner seller-listing-collapse__body seller-listing-collapse__body--modern seller-listing-offers">
                            <ListingOffers
                              listingId={listing.id}
                              askingPrice={listing.price}
                              token={token}
                              onAccepted={() => reload()}
                              onOpenOfferDetail={onOpenOfferDetail}
                            />
                          </div>
                          </div>
                        </details>
                      ) : null
                    }
                  />
                </div>
                </div>
              </details>
            </article>
          )
        })}
      </div>
    </section>
  )
}
